import { Play, Download, Trash2, FileText, Loader2, CheckCircle, AlertCircle, Package } from 'lucide-react';
import { DocumentFile } from '../types';
import { DocumentSettings } from '@/types';

interface DocumentListCompactProps {
  documents: DocumentFile[];
  settings: DocumentSettings;
  isProcessing: boolean;
  onProcessDocument: (document: DocumentFile) => void;
  onProcessAll: () => void;
  onDownloadDocument: (document: DocumentFile) => void;
  onDownloadAll: () => void;
  onDownloadAllAsZip: () => void;
  onRemoveDocument: (id: string) => void;
  onClearAll: () => void;
  isDarkMode: boolean;
}

export default function DocumentListCompact({
  documents,
  settings,
  isProcessing,
  onProcessDocument,
  onProcessAll,
  onDownloadDocument,
  onDownloadAll,
  onDownloadAllAsZip,
  onRemoveDocument,
  onClearAll,
  isDarkMode
}: DocumentListCompactProps) {
  if (documents.length === 0) {
    return null;
  }

  const pendingDocuments = documents.filter(doc => doc.status === 'pending');
  const processingDocuments = documents.filter(doc => doc.status === 'processing');
  const completedDocuments = documents.filter(doc => doc.status === 'completed');

  const getStatusIcon = (status: DocumentFile['status']) => {
    switch (status) {
      case 'pending':
        return <FileText className="w-4 h-4 text-blue-500" />;
      case 'processing':
        return <Loader2 className="w-4 h-4 text-yellow-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleProcessAll = () => {
    console.log('🚀 BOTÃO CONVERTER TODOS CLICADO! (DocumentListCompact)');
    console.log('📊 Estado atual no DocumentListCompact:', {
      totalDocuments: documents.length,
      pendingDocuments: pendingDocuments.length,
      isProcessing,
      documents: documents.map(doc => ({
        id: doc.id,
        name: doc.file?.name,
        status: doc.status,
        hasFile: !!doc.file
      }))
    });
    onProcessAll();
  };

  return (
    <div className="w-full space-y-6">
      {/* Header com Controles */}
      <div className={`hidden lg:block ${
        isDarkMode ? 'bg-[#2A2E3F] border-[#4A4F64]' : 'bg-white border-[#E5E5E5]'
      } border rounded-lg sm:rounded-xl lg:rounded-2xl p-3 sm:p-4 mb-4 sm:mb-6`}>
        <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3">
          {/* Lado Esquerdo: Título e Estatísticas */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <h3 className={`text-lg sm:text-xl font-semibold ${
              isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
            }`}>
              <span className="hidden sm:inline">Documentos Carregados</span>
              <span className="sm:hidden">Documentos</span>
            </h3>
            
            {/* Estatísticas Compactas */}
            <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-xs">
              <span className={`flex items-center space-x-1 px-1.5 sm:px-2 py-1 rounded-full ${
                isDarkMode ? 'text-blue-400 bg-blue-500/10' : 'text-blue-600 bg-blue-100'
              }`}>
                <span>{documents.length} total</span>
              </span>
              {processingDocuments.length > 0 && (
                <span className={`flex items-center space-x-1 px-1.5 sm:px-2 py-1 rounded-full ${
                  isDarkMode ? 'text-yellow-400 bg-yellow-500/10' : 'text-yellow-600 bg-yellow-100'
                }`}>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span className="hidden sm:inline">{processingDocuments.length} processando</span>
                  <span className="sm:hidden">{processingDocuments.length}</span>
                </span>
              )}
              {completedDocuments.length > 0 && (
                <span className={`flex items-center space-x-1 px-1.5 sm:px-2 py-1 rounded-full ${
                  isDarkMode ? 'text-green-400 bg-green-500/10' : 'text-green-600 bg-green-100'
                }`}>
                  <CheckCircle className="w-3 h-3" />
                  <span className="hidden sm:inline">{completedDocuments.length} concluído{completedDocuments.length !== 1 ? 's' : ''}</span>
                  <span className="sm:hidden">{completedDocuments.length}</span>
                </span>
              )}
            </div>
          </div>
          
          {/* Lado Direito: Controles e Ações */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Botões de Ação */}
            <div className="flex flex-wrap items-center gap-1 sm:gap-1.5">
              {completedDocuments.length > 0 && (
                <>
                  <button
                    onClick={onDownloadAll}
                    className="flex items-center space-x-1 px-2 sm:px-3 py-1.5 sm:py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 min-h-[32px] sm:min-h-[36px] touch-manipulation active:scale-95 hover:shadow-md"
                    title={`Baixar todos (${completedDocuments.length})`}
                  >
                    <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Baixar Todos</span>
                    <span className="sm:hidden">Baixar</span>
                    <span className="text-xs opacity-75">({completedDocuments.length})</span>
                  </button>
                  
                  <button
                    onClick={onDownloadAllAsZip}
                    className="flex items-center space-x-1 px-2 sm:px-3 py-1.5 sm:py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 min-h-[32px] sm:min-h-[36px] touch-manipulation active:scale-95 hover:shadow-md"
                    title="Baixar como ZIP"
                  >
                    <Package className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>ZIP</span>
                  </button>
                </>
              )}
              
              {pendingDocuments.length > 0 && !isProcessing && (
                <button
                  onClick={handleProcessAll}
                  className="flex items-center space-x-1 px-2 sm:px-3 py-1.5 sm:py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 min-h-[32px] sm:min-h-[36px] touch-manipulation active:scale-95 hover:shadow-md"
                  title={`Converter todos (${pendingDocuments.length})`}
                >
                  <Play className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Converter Todos</span>
                  <span className="sm:hidden">Converter</span>
                  <span className="text-xs opacity-75">({pendingDocuments.length})</span>
                </button>
              )}
              
              <button
                onClick={onClearAll}
                className={`flex items-center space-x-1 px-2 sm:px-3 py-1.5 sm:py-2 ${
                  isDarkMode ? 'bg-red-500/20 hover:bg-red-500/30 text-red-300' : 'bg-red-100 hover:bg-red-200 text-red-600'
                } rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 min-h-[32px] sm:min-h-[36px] touch-manipulation active:scale-95 hover:shadow-md`}
                title="Limpar tudo"
              >
                <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Limpar Tudo</span>
                <span className="sm:hidden">X</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Documentos */}
      <div className="space-y-2 sm:space-y-3">
        {documents.map((document) => (
          <div
            key={document.id}
            className={`flex items-center justify-between p-3 sm:p-4 rounded-lg sm:rounded-xl border transition-colors ${
              isDarkMode 
                ? 'bg-[#2A2E3F] border-[#4A4F64] hover:bg-[#383D52]' 
                : 'bg-white border-[#E5E5E5] hover:bg-[#F8F9FA]'
            }`}
          >
            {/* Info do Documento */}
            <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
              {/* Preview */}
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0">
                {document.preview ? (
                  <img
                    src={document.preview}
                    alt={document.file.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400" />
                  </div>
                )}
              </div>

              {/* Detalhes */}
              <div className="flex-1 min-w-0">
                <div className={`font-medium text-sm sm:text-base truncate hidden lg:block ${
                  isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                }`}>
                  {document.file.name}
                </div>
                <div className={`text-xs sm:text-sm ${
                  isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'
                }`}>
                  {(() => {
                    const currentFormat = document.status === 'completed' ? settings.format : document.originalFormat;
                    return (
                      <>
                        {formatFileSize(document.file.size)} • {currentFormat.toUpperCase()}
                        {document.pageCount && (
                          <span className="hidden sm:inline"> • {document.pageCount} página{document.pageCount !== 1 ? 's' : ''}</span>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Status - Mobile & Tablet */}
                <div className="flex items-center space-x-1.5 mt-1 lg:hidden">
                  {getStatusIcon(document.status)}
                  <span className={`text-xs ${
                    isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'
                  }`}>
                    {document.status === 'pending' && 'Pendente'}
                    {document.status === 'processing' && 'Processando...'}
                    {document.status === 'completed' && 'Concluído'}
                    {document.status === 'error' && 'Erro'}
                  </span>
                </div>

                {/* Status - Desktop */}
                <div className="hidden lg:flex items-center space-x-2 mt-2 flex-shrink-0">
                  {getStatusIcon(document.status)}
                  <span className={`text-sm ${
                    isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'
                  }`}>
                    {document.status === 'pending' && 'Pendente'}
                    {document.status === 'processing' && 'Processando...'}
                    {document.status === 'completed' && 'Concluído'}
                    {document.status === 'error' && 'Erro'}
                  </span>
                </div>
              </div>
            </div>

            {/* Ações */}
            <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
              {document.status === 'pending' && !isProcessing && (
                <button
                  onClick={() => onProcessDocument(document)}
                  className="flex items-center justify-center space-x-1 px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-xs font-medium transition-all duration-300"
                >
                  <Play className="w-3 h-3" />
                  <span>Converter</span>
                </button>
              )}

              {document.status === 'completed' && (
                <button
                  onClick={() => onDownloadDocument(document)}
                  className="flex items-center justify-center space-x-1 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-medium transition-all duration-300"
                >
                  <Download className="w-3 h-3" />
                  <span>Baixar</span>
                </button>
              )}

              {/* Remover - Mobile/Tablet */}
              <button
                onClick={() => onRemoveDocument(document.id)}
                className={`flex lg:hidden items-center justify-center w-8 h-8 rounded-lg transition-all duration-300 ${
                  isDarkMode ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400' : 'bg-red-100 hover:bg-red-200 text-red-600'
                }`}
                title="Remover"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>

              {/* Botão Remover (Desktop) */}
              <button
                onClick={() => onRemoveDocument(document.id)}
                className={`hidden lg:flex p-2 rounded-lg transition-all duration-300 items-center justify-center w-8 h-8 ${
                  isDarkMode 
                  ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400' 
                  : 'bg-red-100 hover:bg-red-200 text-red-600'
                }`}
                title="Remover"
              >
                <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
} 